import React, { useCallback, useEffect, useState, useMemo } from 'react'
import {useSelector} from 'react-redux';
import { useLocation } from 'react-router-dom';
import GamePrev from './GamePrev';
import AuthProtected from '../../HOCs/AuthProtected';
import { formatDatetimeString } from '../../../utils/general';
import { nflTeams } from '../../../utils/nflUtils';
import { nbaTeams } from '../../../utils/nbaUtils';
import { mlbTeams } from '../../../utils/mlbUtils';
import UpcomingGame from '../Game/UpcomingGame';

// images 
import BaseballIcon from '@assets/images/icons/sports/baseball.svg?react'
import FootballIcon from '@assets/images/icons/sports/football.svg?react'
import BasketballIcon from '@assets/images/icons/sports/basketball.svg?react'

const UpcomingGames = () => {
    const location = useLocation();
    const matches = useSelector((state) => state.user.matches);
    const mlbUpcoming = useSelector((state) => state.mlb.upcoming);
    const nflUpcoming = useSelector(state => state.nfl.upcoming);
    const nbaUpcoming = useSelector(state => state.nba.upcoming);
    const [sport, setSport] = useState(location.state?.sport || "mlb");
    const [selectedGame, setSelectedGame] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showList, setShowList] = useState(true);
    
    // Add resize listener
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate match counts once when matches change
    const matchCounts = useMemo(() => {
        const counts = {};
        if (Array.isArray(matches)) {
            for(const match of matches){
                if(match["game_id"] in counts){
                    counts[match["game_id"]] += 1;
                } else {
                    counts[match["game_id"]] = 1;
                }
            }
        }
        return counts;
    }, [matches]);

    // Calculate upcoming games based on sport and match counts
    const upcoming = useMemo(() => {
        let tmpUpcoming = [];
        if(sport === "mlb" && mlbUpcoming){
            tmpUpcoming = mlbUpcoming.map(game => {
                const matchCount = matchCounts[game._id] || null;
                const homeTeam = mlbTeams[game.home.team];
                const awayTeam = mlbTeams[game.away.team];
                const dateObj = formatDatetimeString(game.date);
                return { ...game, homeTeam, awayTeam, dateObj, matchCount };
            });
        } else if(sport === "nfl" && nflUpcoming){
            tmpUpcoming = nflUpcoming.map(game => {
                const matchCount = matchCounts[game._id] || null;
                const homeTeam = nflTeams[game.home.team_id];
                const awayTeam = nflTeams[game.away.team_id];
                const dateObj = formatDatetimeString(game.date);
                return { ...game, homeTeam, awayTeam, dateObj, matchCount };
            });            
        } else if(sport === "nba" && nbaUpcoming){
            tmpUpcoming = nbaUpcoming.map(game => {
                const matchCount = matchCounts[game._id] || null;
                const homeTeam = nbaTeams[game.home.team_id];
                const awayTeam = nbaTeams[game.away.team_id];
                const dateObj = formatDatetimeString(game.date);
                return { ...game, homeTeam, awayTeam, dateObj, matchCount };
            });  
        }
        return tmpUpcoming;
    }, [sport, mlbUpcoming, nbaUpcoming, nflUpcoming, matchCounts]);

    // Reset Selected Game when sport changes
    useEffect(() => {
        setSelectedGame(null);
    }, [sport]);

    // Select a game to display in detail panel
    const selectGame = useCallback((game) => {
        setSelectedGame(game);
        if (isMobile) {
            setShowList(false);
        }
    }, [isMobile]);

    // Go back to list view on mobile
    const backToList = useCallback(() => {
        setShowList(true);
    }, []);

    // Check location state for game selection when navigating back
    useEffect(() => {
        if (location.state?.game) {
            setSelectedGame(location.state.game);
            console.log(location.state.sport);
            setSport(location.state.sport || 'mlb');
            setShowList(false);
        }
    }, [location.state, location.pathname]);

    return (
        <div className="bg-white dark:bg-brand-dark-900 min-h-screen">
            <div className="flex flex-col lg:flex-row h-screen">
                {/* Left Column - Games List */}
                {(!isMobile || showList) && (
                    <aside className="lg:w-72 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 bg-white dark:bg-brand-dark-900 flex flex-col">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Upcoming Games</h2>
                            <div className="mt-4 flex gap-2">
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "mlb" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("mlb")}>
                                    <BaseballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>MLB</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "nfl" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nfl")}>
                                    <FootballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>NFL</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "nba" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nba")}>
                                    <BasketballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>NBA</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {upcoming && upcoming.length > 0 ? (
                                <div className="space-y-2">
                                    {upcoming.map((game) => (
                                        <GamePrev 
                                            key={game._id} 
                                            game={game} 
                                            sport={sport} 
                                            onClick={() => selectGame(game)}
                                            isActive={selectedGame && selectedGame._id === game._id}
                                        />                 
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <h6 className="text-gray-500 dark:text-gray-400">No Upcoming Games</h6>
                                </div>
                            )}
                        </div>
                    </aside>
                )}
                
                {/* Main Content - Game Details */}
                {(!isMobile || !showList) && (
                    <main className="flex-1 overflow-y-auto">
                        {/* Game Details Content */}
                        {(!isMobile || !showList) && selectedGame ? (
                            <div className="p-4">
                                <UpcomingGame 
                                    game={selectedGame} 
                                    sport={sport} 
                                    location={{ state: { game: selectedGame, sport } }}
                                    params={{ id: selectedGame._id }}
                                    isMobile={isMobile}
                                    backToList={backToList}
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full p-4">
                                <div className="text-center">
                                    <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">Select a game from the list</h3>
                                    <p className="mt-1 text-gray-500 dark:text-gray-400">Game details will appear here</p>
                                </div>
                            </div>
                        )}
                    </main>
                )}
            </div>
        </div>
    )
}

export default AuthProtected(UpcomingGames);